import React from 'react'
import {
  Box, Typography, Slider, Switch, FormControlLabel, Paper, Stack, Divider,
  Select, MenuItem, FormControl, InputLabel, Alert, ListItemText, Chip,
} from '@mui/material'
import { PromptSettings } from '../../../types/gotypes'
import { CodingModelOption, findCodingModel } from '../../../types/codingModels'

// Indicative £/1M-token prices, keyed by CANONICAL model id. This is a
// presentation aid for the estimate below, not a billing source. The numbers
// are goapi/pkg/pricing/pricing.go KnownModels (USD per MTok) x 0.78 (the
// PLATINUM_BUDGET_USD_TO_GBP default); goapi's TestAuditA10_08 fails if they
// drift. GET /token-usage/pricing serves the live card if this ever needs to
// read it. A model with no entry here shows its settings and omits the cost
// line rather than quoting a number we made up. A Bedrock lane is priced at
// the EU profile's tier (1.1x list), which is what it is billed at.
const MODEL_PRICING: Record<string, { input: number; output: number }> = {
  // The AutoCoder default (#1955): $0.11/$0.55 per MTok in eu-west-2.
  'bedrock:claude-haiku-5-5': { input: 0.09, output: 0.43 },
  'azure:gpt-4.1':      { input: 1.56, output: 6.24 },
  'azure:gpt-4.1-nano': { input: 0.08, output: 0.31 },
  'azure:gpt-5':        { input: 0.98, output: 7.80 },
  'azure:gpt-5-mini':   { input: 0.20, output: 1.56 },
  'azure:gpt-5-nano':   { input: 0.04, output: 0.31 },
}

interface Props {
  settings: PromptSettings
  onChange: (settings: PromptSettings) => void
  recordCount: number
  frameworkCount: number
  loopedFrameworkCount: number
  disabled?: boolean
  /** From useCodingModels — the catalog filtered by real credentials. */
  models: CodingModelOption[]
  modelsLoading?: boolean
  modelsError?: string | null
  /** The job's cascade default, preselected when the pipeline has not chosen. */
  defaultModel?: string
  /** Hide the section heading when embedded in a page that supplies its own. */
  hideHeading?: boolean
}

// Per-record token estimates from testing
const TOKENS_PER_RECORD_COMBINED = 119
const TOKENS_PER_RECORD_SLIM = 191
const EXPLANATION_MULTIPLIER = 2

const estimateCost = (
  recordCount: number,
  combinedMode: boolean,
  includeExplanations: boolean,
  pricing: { input: number; output: number } | undefined,
) => {
  const tokensPerRecord = combinedMode ? TOKENS_PER_RECORD_COMBINED : TOKENS_PER_RECORD_SLIM
  const baseTokens = recordCount * tokensPerRecord

  const inputTokens = Math.round(baseTokens * 0.67)
  let outputTokens = Math.round(baseTokens * 0.33)

  if (includeExplanations) {
    outputTokens *= EXPLANATION_MULTIPLIER
  }

  const totalCost = pricing
    ? (inputTokens * pricing.input + outputTokens * pricing.output) / 1_000_000
    : undefined

  return { inputTokens, outputTokens, totalCost }
}

const PipelinePromptSettingsStep: React.FC<Props> = ({
  settings,
  onChange,
  recordCount,
  frameworkCount,
  loopedFrameworkCount,
  disabled = false,
  models,
  modelsLoading = false,
  modelsError = null,
  defaultModel,
  hideHeading = false,
}) => {
  const nonLoopedCount = frameworkCount - loopedFrameworkCount
  const canUseCombinedMode = nonLoopedCount > 1

  // What the pipeline stored, which may be a legacy alias ("4") or empty.
  const stored = settings.model_version || ''
  const selected = findCodingModel(models, stored)

  // The value the Select renders. An empty setting shows the job's cascade
  // default (what coding WOULD use), so the box never reads as "no model" when
  // a model is in fact resolved for it.
  const selectValue = selected?.id || (stored ? '' : defaultModel || '')

  // A stored model no catalog entry claims. Shown, never silently replaced —
  // a pipeline pointing at a model we cannot resolve is the thing to surface.
  const unknownStored = !!stored && !selected

  // Only what can actually be picked. A model with no credentials is not a
  // choice a user can make, and listing ten greyed-out rows buries the two that
  // work. The ONE exception is a model this pipeline is already set to: hiding
  // that would render a saved setting as an empty box and let the next save
  // silently rewrite it, so it stays (disabled, with the alert above saying
  // why).
  const options = models.filter(m => m.available || m.id === selected?.id)

  const pricing = MODEL_PRICING[selectValue]
  const { inputTokens, outputTokens, totalCost } = estimateCost(
    recordCount,
    settings.combined_mode && canUseCombinedMode,
    settings.include_explanations,
    pricing,
  )

  const handleBatchSizeChange = (_: Event, value: number | number[]) => {
    onChange({ ...settings, batch_size: value as number })
  }

  const handleModelChange = (value: string) => {
    onChange({ ...settings, model_version: value })
  }

  const handleCombinedModeChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    onChange({ ...settings, combined_mode: checked })
  }

  const handleExplanationsChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    onChange({ ...settings, include_explanations: checked })
  }

  return (
    <Box sx={{ overflow: 'auto', flex: 1 }} data-testid="autocoder-pipeline-prompt-settings-step">
      {!hideHeading && (
        <>
          <Typography variant="h6" gutterBottom>Prompt Settings</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure how the AI processes your data. These settings affect cost and quality.
          </Typography>
        </>
      )}

      <Stack spacing={3} sx={{ maxWidth: 600 }}>
        {/* Model Selection */}
        <Box>
          {modelsError && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Could not load the model list ({modelsError}). The saved setting is unchanged.
            </Alert>
          )}
          {unknownStored && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              This pipeline is set to <strong>{stored}</strong>, which is not a model
              this build recognises. Coding will fail until a model is chosen below.
            </Alert>
          )}
          {selected && !selected.available && (
            <Alert severity="error" sx={{ mb: 1 }}>
              <strong>{selected.label}</strong> is selected but not configured in this
              environment{selected.unavailable_reason ? ` — ${selected.unavailable_reason}` : '.'}
              {' '}Coding batches sent to it will fail. Choose an available model.
            </Alert>
          )}
          <FormControl fullWidth size="small" disabled={disabled || modelsLoading}>
            <InputLabel id="pipeline-prompt-settings-model-label">Model</InputLabel>
            <Select
              labelId="pipeline-prompt-settings-model-label"
              value={selectValue}
              label="Model"
              onChange={(e) => handleModelChange(e.target.value)}
              // On the display element (`div[role=combobox]`), not the Select
              // wrapper: a testid handed to Select lands on the OutlinedInput
              // root, and `inputProps` would land on the aria-hidden native
              // input — neither is the thing a test clicks to open the menu.
              // The cast is the MUI type story, not a smell: SelectDisplayProps
              // is a plain `HTMLAttributes<HTMLDivElement>`, which carries no
              // index signature, so a fresh literal trips the excess-property
              // check on any `data-*` key (TextField escapes this only because
              // its htmlInput slot IS index-signed).
              SelectDisplayProps={
                { 'data-testid': 'autocoder-pipeline-model-select' } as React.HTMLAttributes<HTMLDivElement>
              }
            >
              {options.map((model) => (
                <MenuItem
                  key={model.id}
                  value={model.id}
                  disabled={!model.available}
                  data-testid={`autocoder-pipeline-model-option-${model.id}`}
                >
                  <ListItemText
                    primary={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {model.label}
                        {model.reasoning && <Chip label="reasoning" size="small" variant="outlined" />}
                        {!model.available && <Chip label="not configured" size="small" color="default" />}
                      </Box>
                    }
                    secondary={model.available ? undefined : model.unavailable_reason}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {modelsLoading
              ? 'Loading the model catalogue…'
              : !stored && defaultModel
                ? `Not set on this pipeline — coding uses this job's default (${
                  findCodingModel(models, defaultModel)?.label || defaultModel
                }).`
                : 'Models configured in this environment.'}
          </Typography>
        </Box>

        <Divider />

        {/* Batch Size */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Batch Size: {settings.batch_size} records per call
          </Typography>
          <Slider
            value={settings.batch_size}
            onChange={handleBatchSizeChange}
            aria-label="Batch size"
            min={10}
            max={100}
            step={10}
            marks={[
              { value: 10, label: '10' },
              { value: 50, label: '50' },
              { value: 100, label: '100' },
            ]}
            disabled={disabled}
            color="primary"
          />
          <Typography variant="caption" color="text.secondary">
            More records per call = lower overhead, but larger calls may be slower
          </Typography>
        </Box>

        {/*
          There is deliberately no Concurrency control here. How many batches run
          at once belongs to the MODEL LANE, not the pipeline: each lane derives
          its in-flight cap from its own rate limits and the dispatcher paces
          against that. The old slider was written into pipeline config and read
          by nothing at all. See the Activity tab for real lane throughput.
        */}

        <Divider />

        {/* Combined Mode */}
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={settings.combined_mode}
                onChange={handleCombinedModeChange}
                disabled={disabled || !canUseCombinedMode}
                color="primary"
                // On the `<input type="checkbox">`, not the Switch's root span:
                // that input is what carries the checked state a test asserts
                // and what a click has to reach to toggle it. Same cast, same
                // reason as the Select above — the input slot's props are typed
                // `InputHTMLAttributes`, which has no index signature for
                // `data-*`.
                slotProps={{
                  input: { 'data-testid': 'autocoder-pipeline-combined-mode' } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
              />
            }
            label="Combined Mode"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6 }}>
            {canUseCombinedMode
              ? `Classify all ${nonLoopedCount} non-looped frameworks in a single call. Reduces cost by ~60%.`
              : nonLoopedCount <= 1
                ? 'Requires 2+ non-looped frameworks to combine.'
                : 'Combined mode is available.'
            }
          </Typography>
          {loopedFrameworkCount > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: 0.5 }}>
              {loopedFrameworkCount} looped framework{loopedFrameworkCount > 1 ? 's' : ''} will always run individually (multi-phase).
            </Typography>
          )}
        </Box>

        <Divider />

        {/* Include Explanations */}
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={settings.include_explanations}
                onChange={handleExplanationsChange}
                disabled={disabled}
                color="primary"
                slotProps={{
                  input: { 'data-testid': 'autocoder-pipeline-include-explanations' } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
              />
            }
            label="Include Explanations"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6 }}>
            For non-looped frameworks, include the matching text and explanation for each assigned code. Useful for reviewing why codes were assigned, but roughly doubles output token cost. Looped frameworks always include matching text (required for multi-phase classification).
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: 0.5 }}>
            This applies to records coded from now on. Records already coded keep
            whatever was asked for at the time — it cannot be added retrospectively
            without re-coding them.
          </Typography>
        </Box>

        <Divider />

        {/* Cost Estimate */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Cost Estimate</Typography>
          {recordCount === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Import data first to see cost estimates.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {totalCost !== undefined && pricing ? (
                <>
                  <Typography variant="body2">
                    Estimated cost: <strong>{totalCost < 0.01 ? '<£0.01' : `£${totalCost.toFixed(2)}`}</strong> for {recordCount.toLocaleString()} records
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Pricing: £{pricing.input}/M input, £{pricing.output}/M output ({selected?.label || selectValue})
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No published price for {selected?.label || selectValue || 'this model'}, so no cost
                  estimate is shown.
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                ~{inputTokens.toLocaleString()} input tokens + ~{outputTokens.toLocaleString()} output tokens
              </Typography>
            </Stack>
          )}
        </Paper>
      </Stack>
    </Box>
  )
}

export default PipelinePromptSettingsStep
